import { Link, useParams } from "react-router-dom";
import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import VerdictBadge from "@/components/base/VerdictBadge";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { gossipList } from "@/mocks/gossip";
import { brand } from "@/mocks/site";
import { formatHeat } from "@/utils/verdict";
import ScoreCard from "@/pages/gossip/detail/components/ScoreCard";
import EvidenceChain from "@/pages/gossip/detail/components/EvidenceChain";
import Timeline from "@/pages/gossip/detail/components/Timeline";
import RelatedList from "@/pages/gossip/detail/components/RelatedList";

function NotFoundItem() {
  return (
    <div className="max-w-shell mx-auto px-4 md:px-6 py-20 md:py-28 text-center">
      <i className="ri-ghost-line text-4xl text-foreground-300"></i>
      <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条爆料不存在</h1>
      <p className="mt-3 text-sm text-foreground-600">
        它可能已被合并或更新，去爆料库看看最新的鉴定结论吧。
      </p>
      <Link
        to="/gossip"
        className="mt-6 inline-block px-6 py-3 rounded-control bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
      >
        返回爆料库
      </Link>
    </div>
  );
}

export default function GossipDetail() {
  const { id } = useParams();
  const item = gossipList.find((g) => g.id === id);

  const related = item
    ? gossipList.filter((g) => g.id !== item.id && g.category === item.category).slice(0, 4)
    : [];
  const fallbackRelated = item
    ? gossipList.filter((g) => g.id !== item.id).slice(0, 4)
    : [];

  const jsonLd = item
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            headline: item.title,
            description: item.summary,
            datePublished: item.publishedAt,
            dateModified: item.publishedAt,
            inLanguage: "zh-CN",
            articleSection: item.category,
            keywords: item.tags.join(","),
            image: item.cover,
            author: { "@type": "Organization", name: brand.name, url: SITE_ORIGIN },
            publisher: { "@type": "Organization", name: brand.name, url: SITE_ORIGIN },
            mainEntityOfPage: `${SITE_ORIGIN}/gossip/${item.id}`,
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
              { "@type": "ListItem", position: 2, name: "爆料库", item: `${SITE_ORIGIN}/gossip` },
              {
                "@type": "ListItem",
                position: 3,
                name: item.title,
                item: `${SITE_ORIGIN}/gossip/${item.id}`,
              },
            ],
          },
        ],
      }
    : undefined;

  useSeo({
    title: item
      ? `${item.title} | 鉴定结论与证据链 · 瓜田爆料`
      : "爆料详情 | 瓜田爆料",
    description: item
      ? `可信度 ${item.credibility} 分，结论：${item.verdict}。${item.summary.slice(0, 70)}`
      : "查看爆料真假鉴定结论、证据链与时间线。",
    keywords: item ? [...item.tags, item.category, "真假鉴定", "实锤分析"] : ["真假鉴定", "实锤分析"],
    path: `/gossip/${id ?? ""}`,
    ogType: "article",
    jsonLd,
  });

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <ScrollToTop />
        <SiteHeader />
        <main className="flex-1">
          <NotFoundItem />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const relatedFinal = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="border-b border-background-200 bg-background-100 paper-texture">
            <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-10">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "爆料库", to: "/gossip" },
                  { label: item.category, to: `/gossip?cat=${encodeURIComponent(item.category)}` },
                  { label: "鉴定详情" },
                ]}
              />

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <VerdictBadge verdict={item.verdict} />
                <span className="px-2.5 py-1 rounded-control bg-secondary-100 text-secondary-800 text-xs">
                  {item.category}
                </span>
                <span className="font-mono-num text-xs text-foreground-400">
                  档案编号 {item.id.toUpperCase()}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug tracking-tight max-w-4xl">
                {item.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  发布 {item.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line"></i>
                  热度 {formatHeat(item.heat)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  约 {item.readMinutes} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-links-line"></i>
                  {item.evidence.length} 条证据 · {item.sources.length} 个来源
                </span>
              </div>

              <ul className="mt-4 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <li
                    key={tag}
                    className="px-2.5 py-1 rounded-control border border-background-300 bg-background-50 text-[11px] text-foreground-600"
                  >
                    #{tag}
                  </li>
                ))}
              </ul>
            </div>
          </header>

          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-8 space-y-10">
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    结论摘要
                  </h2>
                  <div className="mt-4 rounded-card border border-primary-200 bg-primary-50 p-5">
                    <p className="text-sm md:text-[15px] text-foreground-800 leading-relaxed">
                      {item.conclusion}
                    </p>
                  </div>
                  <p className="mt-4 text-sm md:text-[15px] text-foreground-600 leading-relaxed">
                    {item.summary}
                  </p>
                </section>

                <section id="evidence" className="scroll-mt-28">
                  <div className="flex items-end justify-between gap-4 mb-4">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                      证据链整理
                    </h2>
                    <span className="text-xs text-foreground-400">
                      共 {item.evidence.length} 条，按权重排布
                    </span>
                  </div>
                  <EvidenceChain evidence={item.evidence} />
                </section>

                <section id="timeline" className="scroll-mt-28">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                    事件时间线
                  </h2>
                  <Timeline items={item.timeline} />
                </section>

                <section id="sources" className="scroll-mt-28">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    来源与回应
                  </h2>
                  <div className="border border-background-200 rounded-card overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-background-100 text-foreground-600">
                        <tr>
                          <th className="text-left font-medium px-4 md:px-5 py-3">来源</th>
                          <th className="text-left font-medium px-4 md:px-5 py-3">类型</th>
                          <th className="text-left font-medium px-4 md:px-5 py-3 hidden sm:table-cell">说明</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200">
                        {item.sources.map((source) => (
                          <tr key={source.name}>
                            <td className="px-4 md:px-5 py-3 text-foreground-900">{source.name}</td>
                            <td className="px-4 md:px-5 py-3 text-foreground-600">{source.type}</td>
                            <td className="px-4 md:px-5 py-3 text-foreground-500 hidden sm:table-cell">
                              {source.note}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
                    材料展示遵循最小必要原则：涉及个人隐私、联系方式或可能造成二次伤害的内容不予公开，
                    仅描述可核验要点。
                  </p>
                </section>

                <section className="rounded-card border border-background-200 bg-background-100 p-5 md:p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    发现结论有误？
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    带上可核验的反证材料提交纠错，核实后我们会公开更新结论，并保留修改记录。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1 px-5 py-2.5 rounded-control bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
                  >
                    提交纠错反馈
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </section>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 space-y-6">
                  <ScoreCard item={item} />
                  <RelatedList items={relatedFinal} />
                  <div className="rounded-card border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      快速导航
                    </h2>
                    <ul className="mt-3 space-y-2 text-sm">
                      {[
                        { href: "#evidence", label: "证据链整理" },
                        { href: "#timeline", label: "事件时间线" },
                        { href: "#sources", label: "来源与回应" },
                      ].map((link) => (
                        <li key={link.href}>
                          <a
                            href={link.href}
                            className="flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors"
                          >
                            <i className="ri-subtract-line text-foreground-300"></i>
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}